import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  MenuItem,
  CircularProgress,
  Divider,
} from "@material-ui/core";
import { Delete } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";

const statusColor = (st) => {
  const s = String(st || "").toUpperCase();
  if (s === "APPROVED") return "#16a34a";
  if (s === "PENDING") return "#d97706";
  if (s === "REJECTED" || s === "DISABLED") return "#dc2626";
  return "#6b7280";
};

const PilotTemplatesModal = ({ open, onClose, whatsAppId }) => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("UTILITY");
  const [bodyText, setBodyText] = useState("");
  const [footer, setFooter] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!whatsAppId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/pilot/${whatsAppId}/templates`);
      const arr = Array.isArray(data) ? data : data?.data || [];
      setTemplates(arr);
    } catch (err) {
      toast.error("Erro ao carregar modelos.");
    } finally {
      setLoading(false);
    }
  }, [whatsAppId]);

  useEffect(() => {
    if (open) {
      load();
      setName("");
      setBodyText("");
      setFooter("");
      setCategory("UTILITY");
    }
  }, [open, load]);

  const handleCreate = async () => {
    if (!name.trim() || !bodyText.trim()) {
      toast.warn("Preencha o nome e o texto da mensagem.");
      return;
    }
    const slug = name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    const vars = (bodyText.match(/{{\s*([a-zA-Z0-9_]+)\s*}}/g) || []).map((v) =>
      v.replace(/[{}\s]/g, "")
    );
    const examples = {};
    vars.forEach((v) => {
      examples[v] = "exemplo";
    });
    const payload = {
      name: slug,
      category,
      language: "pt_BR",
      body: {
        body: { text: bodyText },
        ...(footer.trim() ? { footer: { text: footer.trim() } } : {}),
      },
      examples,
    };
    setSaving(true);
    try {
      await api.post(`/pilot/${whatsAppId}/templates`, payload);
      toast.success("Modelo enviado para aprovação da Meta.");
      setName("");
      setBodyText("");
      setFooter("");
      load();
    } catch (err) {
      const msg =
        err?.response?.data?.error || err?.message || "Erro ao criar modelo";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este modelo?")) return;
    try {
      await api.delete(`/pilot/${whatsAppId}/templates/${id}`);
      toast.success("Modelo removido.");
      load();
    } catch (err) {
      toast.error("Erro ao remover modelo.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Modelos de mensagem</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" style={{ color: "#6b7280", marginBottom: 12 }}>
          Modelos são aprovados pela Meta e usados para enviar mensagem quando a
          janela de 24h estiver fechada (ex: retomar conversa depois de alguns
          dias). Use {"{{nome}}"} para variáveis.
        </Typography>

        <Typography variant="subtitle2" style={{ marginBottom: 6 }}>
          Seus modelos
        </Typography>
        {loading ? (
          <CircularProgress size={22} />
        ) : templates.length === 0 ? (
          <Typography variant="body2" style={{ color: "#9ca3af" }}>
            Nenhum modelo ainda.
          </Typography>
        ) : (
          templates.map((t) => (
            <Box
              key={t.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "6px 0",
                borderBottom: "1px solid #f3f4f6",
              }}
            >
              <Box>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {t.name}
                </Typography>
                <Typography variant="caption" style={{ color: "#9ca3af" }}>
                  {t.category}
                </Typography>
              </Box>
              <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Chip
                  size="small"
                  label={t.metaStatus || t.status || "-"}
                  style={{
                    backgroundColor: statusColor(t.metaStatus || t.status),
                    color: "white",
                  }}
                />
                <IconButton size="small" onClick={() => handleDelete(t.id)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))
        )}

        <Divider style={{ margin: "16px 0" }} />

        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
          Criar novo modelo
        </Typography>
        <TextField
          label="Nome"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
          style={{ marginBottom: 10 }}
          disabled={saving}
          helperText="Identificador (ex: retomada_atendimento)"
        />
        <TextField
          label="Categoria"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          select
          fullWidth
          size="small"
          style={{ marginBottom: 10 }}
          disabled={saving}
        >
          <MenuItem value="UTILITY">Utilitário (retomar conversa, lembrete)</MenuItem>
          <MenuItem value="MARKETING">Marketing (promoção, divulgação)</MenuItem>
          <MenuItem value="OTP">Código (OTP)</MenuItem>
        </TextField>
        <TextField
          label="Texto da mensagem"
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          size="small"
          style={{ marginBottom: 10 }}
          disabled={saving}
          helperText="Use {{nome}} para variáveis. Não comece nem termine com variável."
        />
        <TextField
          label="Rodapé (opcional)"
          value={footer}
          onChange={(e) => setFooter(e.target.value)}
          fullWidth
          size="small"
          disabled={saving}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Fechar
        </Button>
        <Button
          onClick={handleCreate}
          color="primary"
          variant="contained"
          disabled={saving}
        >
          {saving ? <CircularProgress size={20} /> : "Criar e enviar p/ aprovação"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PilotTemplatesModal;
